import { getAdditionalObjectsService } from "./utils.js";

import {
	Math as THREEMath,
	Mesh,
	MeshBasicMaterial,
	BoxHelper
} from '../../../../src/js/three.module.js';


import {
	rotateAroundWorldAxis,
} from '../../GameTools.js';

import { ObjectClickHandler }       from '../../../utils/object-click-handler.js';

import { CommentsHandler }      from '../../services/CommentsService.js';
import UserActionHistoryService from '../../services/UserActionHistoryService.js';
import { GameSceneService } from "../../services/game-scene.service.js";

/**************************************
 *
 * AdditionalObject
 *
 */
export class BaseAdditionalObject {

    service;

    constructor( mesh ) {

        this.service = getAdditionalObjectsService();

        let additionalObjectsService = this.service;

        if (additionalObjectsService.objects[ mesh.uuid ]) throw new Error( "Unable to add AdditionalObject, already existing one with same uid" );
        
        this.setMesh( mesh );

        additionalObjectsService.objects[ this.uuid ] = this;

        window.ContextMenuService.init( "preview-additionalObject-"+this.uuid, "templates/context-modals/additionalObject.html", {
            obj: this,
            objService: additionalObjectsService
        });

        this.mesh.userData.type = "additionalObject";
        this.mesh.userData.additionalObject = this;

        this.isDraggable = true;
        this.selected    = false;

        this.isRemoveable = true;

        this.availPlanes = { floor: true, wall: true };

        this.size = null;

        /**
         * Käyttäjän antama nimi objektille
         */
        this.title = null;

        /**
         * Järjestelmän generoima nimi objektille suhteessa muihi8n objekteihin.
         * Voidaan määrittää esim. hash-ryhmäkohtaisesti.
         */
        this.$altTitle = null;

        new ObjectClickHandler().attachTo( this );

        this.comments = new CommentsHandler( this );

        /********************/
        var z_offset = 0;
        Object.defineProperty( this, "z_offset", {
            set : function(v) { z_offset = parseFloat(v); },
            get : function()  { return z_offset; }
        });

        /********************/
        var scale = 1;
        Object.defineProperty( this, "scale", {
            set : function(v) { scale = parseFloat(v); },
            get : function()  { return scale; }
        });

        /********************/
        Object.defineProperty( this, "$$selected", {
            set : function(v) { this.toggleSelect(this.selected = !!v); },
            get : function()  { return this.selected; }
        });

        /********************/
        var disabled = false;
        Object.defineProperty( this, "disabled", {
            set : function(v) { this.mesh.visible = !(disabled = !!v); },
            get : function()  { return !!disabled; }
        });
    }

    // Vaihdetaan meshia (onko tarpeen?) HUOM! Vaihdetaan meshin uuid
    setMesh( mesh ) {

        let additionalObjectsService = this.service;

        if (mesh) {
            if (!this.mesh) this.uuid = mesh.uuid;

            // Ollaan vaihtamassa nykyistä
            if (this.mesh != mesh) {

                if (this.mesh) {
                    this.mesh.copy( mesh );
                } else {
                    this.mesh = mesh;
                    additionalObjectsService.add( this.mesh );
                }

                this.mesh.uuid = this.uuid;
            }
        }

        if (this.mesh) {

            this.mesh.castShadow = true;
            this.mesh.receiveShadow = true;

            var obj = this;
            this.mesh.traverse(function( child ){
                child.userData.type = "additionalObject";
                child.userData.additionalObject = obj;

                child.castShadow = true;
                child.receiveShadow = true;
            });
        }
    }

    setWireframe( mesh ) {

        mesh = mesh || this.mesh;

        var wireframe = this.wireframe;

        if (!wireframe) {
            if (mesh.geometry) {
                wireframe = new Mesh( mesh.geometry, new MeshBasicMaterial({
                    color     : 0xffff00,
                    wireframe : true
                }));
            } else {
                wireframe = new BoxHelper();
            }
        }
        
        if (wireframe.material) {
            wireframe.material.depthTest   = false;
            wireframe.material.depthWrite  = false;
            wireframe.material.transparent = true;
        }

        this.wireframe = wireframe;
    }

    toggleSelect( val ) {
        if( arguments.length == 0 ) val = !this.selected;

        this.selected = !!val;
        this.onSelectChange();
    }

    onSelectChange() {
        if (this.wireframe) this.wireframe.visible = (GameSceneService.getInstance().editor && this.selected) ? true : false;
    }

    /**
     * Päivitetään ryhmittelyn yksilöivä hash-merkkijono
     */
    refreshHash() {
        let additionalObjectsService = this.service;
        additionalObjectsService.refreshObjs();
    }

    /**
     * Vaihdetaan objektin UUID esimerkiksi latauksen, kloonauksen uutismisen, tms. yhteydessä
     */
    changeUuid( uuid ) {
        
        let additionalObjectsService = this.service;

        if (!uuid) {

            return console.warn( "AdditionalObject.changeUuid() :: No uuid given. Aborting!" );

        } else if (additionalObjectsService.objects[ uuid ]) {

            if (additionalObjectsService.objects[ uuid ] != this) {

                throw new Error("Unable to add AdditionalObject, already existing one with same uid");

            } else {

                return console.warn( "AdditionalObject.changeUuid() :: Not changing uuid to current uuid. Aborting!" );
            }
        }

        delete additionalObjectsService.objects[ this.uuid ];

        additionalObjectsService.objects[ uuid ] = this;

        window.ContextMenuService.changeKey( "preview-additionalObject-"+this.uuid, "preview-additionalObject-"+uuid );

        this.uuid = this.mesh.uuid = uuid;
        this.refreshHash();
    }

    /**
     * Sijoitetaan objektin sijainti tiettyyn yksittäiseen pisteeseen.
     * Objektikohtaisesti myös asentoa voidaan vaihtaa.
     */
    setPlaceByIntersect() {
        console.warn( "Plain AdditionalObject.setPlaceByIntersect not implemented!" );
    }

    /**
     * Totetutetaan käsky "Pyöritä objektia"
     */
    rotateOnPlane( plus ) {
        if (isNaN(plus = plus || 0) || typeof plus != "number") throw "Invalid argument!";
        
        if (this.$lastNormal) {
            rotateAroundWorldAxis( this.mesh, this.$lastNormal, plus);
            UserActionHistoryService.getInstance().do("additionalObject.move", this, null, this.mesh.rotation );
        } else {
            var rot = this.mesh.rotation.clone();
            rot.y += plus;
            UserActionHistoryService.getInstance().do("additionalObject.move", this, null, rot );
        }

        let { entitySelectionService } = window;

        if (entitySelectionService.hilightObject.object && entitySelectionService.hilightObject.object.userData.type == "additionalObject") {
            if (entitySelectionService.hilightObject.object.userData.additionalObject == this) {
                if (entitySelectionService.hilightObject.$helper) {
                    entitySelectionService.hilightObject.$helper.setFromObject( this.mesh );
                }
            }
        }
    }

    /**
     * Merkitään tieto objektin mahdollisesti toteuttamasta mallineesta
     */
    $setTemplate( template ) {

        if (template.$objs.indexOf( this ) == -1) template.$objs.push( this );

        this.$template = template;
    }


    getPrimaryMeshMaterial() {
        return this.getMeshMaterialArray()?.[0] || undefined;
    }

    getMeshMaterialArray() {

        let material = this.mesh.material;

        if (!material) return [];

        return Array.isArray( material ) ? material : [ material ];
    }


} /* /AdditionalObject */